import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ChevronRight,
  History,
  MoreHorizontal,
  PanelRight,
  Settings2,
  Sparkles,
  SquarePen,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  useSalesTeams,
  useSalesTeamsMembers,
} from "@/hooks/queries/use-sales-teams";
import type { IntelligenceTopicRecord } from "@graftus/schema";
import { ScopePicker } from "./ScopePicker";
import type { ChatScope } from "./scope";
import {
  getTopic,
  isCurrentTopicKey,
  type CurrentTopicKey,
  type TopicKey,
} from "./topics";
import { TOPIC_ICONS } from "./topic-icons";
import { cn } from "@/lib/utils";
import { CoachThreadPicker } from "@/components/coach/CoachThreadPicker";

// Shared pill style used for team breadcrumb, topic chip, and nav arrows.
const pillCls =
  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs text-g-ink-500 transition-colors hover:bg-g-primary-wash hover:text-g-ink-900 disabled:pointer-events-none disabled:opacity-50";

interface Props {
  businessId: string;
  scope: ChatScope;
  onScopeChange: (scope: ChatScope) => void;
  scopeLocked?: boolean;
  topicKey: TopicKey;
  onTopicChange: (key: CurrentTopicKey) => void;
  topics: IntelligenceTopicRecord[];
  topicLocked?: boolean;
  threadId?: string | null;
  onDeleteThread?: () => void;
  /** Resumes an earlier conversation picked from the history list. */
  onSelectThread?: (threadId: string) => void;
  hideOrganizationOption?: boolean;
  onOpenContext?: () => void;
  onNewChat?: () => void;
  className?: string;
}

export function ChatScopeHeader({
  businessId,
  scope,
  onScopeChange,
  scopeLocked = false,
  topicKey,
  onTopicChange,
  topics,
  topicLocked = false,
  threadId,
  onDeleteThread,
  onSelectThread,
  hideOrganizationOption = false,
  onOpenContext,
  onNewChat,
  className,
}: Props) {
  const { t } = useTranslation("intelligence");
  const [historyOpen, setHistoryOpen] = useState(false);
  const { data: allMembers = [] } = useSalesTeamsMembers(businessId);
  const { data: teams = [] } = useSalesTeams(businessId);

  const teamById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);

  const teammates = useMemo(() => {
    if (scope.kind !== "member" || !scope.salesTeamId) return [];
    return allMembers.filter((m) => m.salesTeamId === scope.salesTeamId);
  }, [scope, allMembers]);

  const currentIndex = useMemo(() => {
    if (scope.kind !== "member") return -1;
    return teammates.findIndex((m) => m.id === scope.memberId);
  }, [scope, teammates]);

  const navigate = (delta: number) => {
    const next = teammates[currentIndex + delta];
    if (!next) return;
    onScopeChange({
      kind: "member",
      memberId: next.id,
      salesTeamId: next.salesTeamId ?? null,
    });
  };

  const teamId = scope.kind === "member" ? scope.salesTeamId : null;
  const teamName = teamId ? (teamById.get(teamId)?.name ?? null) : null;

  const topic = getTopic(topicKey, topics);
  const TopicIcon = TOPIC_ICONS[topic.key] ?? Sparkles;

  return (
    <div
      className={cn(
        "relative isolate z-20 flex items-center gap-1 px-3 py-1.5",
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -bottom-4 -z-10 bg-gradient-to-b from-card via-card via-65% to-card/0"
      />
      {/* ── Far left: history (GRA-255) + new chat buttons ── */}
      {onSelectThread && (
        <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
          <PopoverTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0 text-g-ink-500"
              title={t("rail.picker.title")}
              aria-label={t("rail.picker.title")}
            >
              <History className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" sideOffset={6} className="w-[300px] p-0">
            <CoachThreadPicker
              businessId={businessId}
              activeThreadId={threadId ?? null}
              onSelect={(id) => {
                setHistoryOpen(false);
                onSelectThread(id);
              }}
              onNewChat={() => {
                setHistoryOpen(false);
                onNewChat?.();
              }}
            />
          </PopoverContent>
        </Popover>
      )}
      {onNewChat && (
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0 text-g-ink-500"
          onClick={onNewChat}
          title="Ny chatt"
          aria-label="Ny chatt"
        >
          <SquarePen className="h-3.5 w-3.5" />
        </Button>
      )}
      {onNewChat && <div className="mx-1 h-4 w-px shrink-0 bg-g-line" />}

      {/* ── Left: breadcrumb (team › scope · topic) ── */}
      <div className="flex min-w-0 flex-1 items-center gap-0.5">
        {teamName && (
          <>
            <button
              type="button"
              disabled={scopeLocked}
              onClick={() =>
                onScopeChange({ kind: "team", salesTeamId: teamId! })
              }
              title={`Byt till team: ${teamName}`}
              className={pillCls}
            >
              <span className="max-w-[100px] truncate">{teamName}</span>
            </button>
            <ChevronRight className="h-3 w-3 shrink-0 text-g-ink-400" />
          </>
        )}

        <span title={scopeLocked ? t("chat.scopeLocked") : undefined}>
          <ScopePicker
            businessId={businessId}
            scope={scope}
            onChange={onScopeChange}
            trigger="chip"
            disabled={scopeLocked}
            hideOrganizationOption={hideOrganizationOption}
          />
        </span>

        {/* prev / next member arrows */}
        {scope.kind === "member" && teammates.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => navigate(-1)}
              disabled={scopeLocked || currentIndex <= 0}
              title="Föregående teammedlem"
              className={pillCls}
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => navigate(1)}
              disabled={scopeLocked || currentIndex >= teammates.length - 1}
              title="Nästa teammedlem"
              className={pillCls}
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </>
        )}

        <span className="mx-1 text-g-ink-400">·</span>

        {topicLocked ? (
          <span
            className={`${pillCls} cursor-default opacity-50 hover:bg-transparent hover:text-g-ink-500`}
            title={t("chat.scopeLocked")}
          >
            <TopicIcon className="h-3 w-3 shrink-0" />
            <span className="max-w-[140px] truncate">{topic.title}</span>
          </span>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={pillCls}>
                <TopicIcon className="h-3 w-3 shrink-0" />
                <span className="max-w-[140px] truncate">{topic.title}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              <DropdownMenuRadioGroup
                value={
                  isCurrentTopicKey(topicKey, topics) ? topicKey : undefined
                }
                onValueChange={(v) => onTopicChange(v as CurrentTopicKey)}
              >
                {topics.map((t) => (
                  <DropdownMenuRadioItem key={t.key} value={t.key}>
                    {t.title}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* ── Right: actions cluster ── */}
      <div className="flex shrink-0 items-center gap-0.5">
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-g-ink-500"
          asChild
          title="Hantera team & teammedlemmar"
        >
          <Link to="/coach/teams">
            <Settings2 className="h-3.5 w-3.5" />
          </Link>
        </Button>

        {onOpenContext && (
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-g-ink-500"
            onClick={onOpenContext}
            title="Visa kontext"
            aria-label="Visa kontext"
          >
            <PanelRight className="h-3.5 w-3.5" />
          </Button>
        )}

        {threadId && onDeleteThread && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-g-ink-500"
                aria-label="Mer"
                title="Mer"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                onClick={onDeleteThread}
                className="text-g-danger focus:text-g-danger"
              >
                <Trash2 className="h-3.5 w-3.5" /> Ta bort tråd
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
